import type { ExecutionStopOption } from './executionDraftTypes';

export type LegacyFlatStop = {
  uid: string;
  type: 'loading' | 'unloading' | 'transfer';
  parentLoadingPosition?: number | null;
  pointType?: string | null;
  pointId?: number | null;
  address?: string | null;
};

/** Position index for legacy (non-group) route create — main load @0, extras @1..N, main unload @N+1. */
export function buildLegacyPositionByStopUid(params: {
  mainLoadingUid: string;
  stops: LegacyFlatStop[];
  mainUnloadingUid: string;
}): Map<string, number> {
  const map = new Map<string, number>();
  map.set(params.mainLoadingUid, 0);
  params.stops.forEach((s, idx) => map.set(s.uid, idx + 1));
  map.set(params.mainUnloadingUid, params.stops.length + 1);
  return map;
}

export function resolveLegacyLoadingStopUid(params: {
  loadingPosition: number;
  mainLoadingUid: string;
  mainLoadingPos: number;
  stops: LegacyFlatStop[];
}): string {
  if (params.loadingPosition === params.mainLoadingPos) return params.mainLoadingUid;
  const stop = params.stops[params.loadingPosition - 1];
  return stop?.uid ?? params.mainLoadingUid;
}

export function cargoFromStopMatchesLoadingPosition(params: {
  fromStopUid: string | undefined;
  loadingPosition: number;
  positionByStopUid: Map<string, number>;
  mainLoadingPos: number;
}): boolean {
  if (!params.fromStopUid) return params.loadingPosition === params.mainLoadingPos;
  const pos = params.positionByStopUid.get(params.fromStopUid);
  if (pos === undefined) return params.loadingPosition === params.mainLoadingPos;
  return pos === params.loadingPosition;
}

export function buildLegacyUnloadingOptionsWithUid(params: {
  stops: LegacyFlatStop[];
  currentLoadingPosition: number;
  mainLoadingPos: number;
  mainUnloadingPos: number;
  mainUnloadingUid: string;
  mainUnloading: {
    pointType?: string | null;
    pointId?: number | null;
  };
  resolveWarehouseLabel?: (id: number) => string;
  resolveApplicationLabel?: (id: number) => string;
  resolveConsigneeLabel?: (id: number) => string;
}): ExecutionStopOption[] {
  const options: ExecutionStopOption[] = [];
  const {
    stops,
    currentLoadingPosition,
    mainLoadingPos,
    mainUnloadingPos,
    mainUnloadingUid,
    mainUnloading,
    resolveWarehouseLabel,
    resolveApplicationLabel,
    resolveConsigneeLabel
  } = params;

  if (currentLoadingPosition === mainLoadingPos) {
    let mainLabel = 'Основная выгрузка';
    if (mainUnloading.pointType === 'warehouse' && mainUnloading.pointId != null) {
      mainLabel =
        resolveWarehouseLabel?.(Number(mainUnloading.pointId)) ?? 'Склад';
    } else if (mainUnloading.pointType === 'application' && mainUnloading.pointId != null) {
      mainLabel =
        resolveApplicationLabel?.(Number(mainUnloading.pointId)) ?? 'Заявка доставки';
    } else if (mainUnloading.pointType === 'consignee' && mainUnloading.pointId != null) {
      mainLabel =
        resolveConsigneeLabel?.(Number(mainUnloading.pointId)) ?? 'Получатель';
    }
    options.push({
      stopUid: mainUnloadingUid,
      label: mainLabel,
      groupUid: 'legacy',
      position: mainUnloadingPos
    });
  }

  stops.forEach((stop, idx) => {
    if (stop.type !== 'unloading') return;
    if (Number(stop.parentLoadingPosition ?? mainLoadingPos) !== currentLoadingPosition) return;

    let desc = stop.address?.trim() || 'Выгрузка';
    if (stop.pointType === 'warehouse' && stop.pointId != null) {
      desc = resolveWarehouseLabel?.(Number(stop.pointId)) ?? 'Склад';
    } else if (stop.pointType === 'application' && stop.pointId != null) {
      desc = resolveApplicationLabel?.(Number(stop.pointId)) ?? 'Заявка доставки';
    } else if (stop.pointType === 'consignee' && stop.pointId != null) {
      desc = resolveConsigneeLabel?.(Number(stop.pointId)) ?? 'Получатель';
    }
    options.push({
      stopUid: stop.uid,
      label: desc,
      groupUid: 'legacy',
      position: idx + 1
    });
  });

  return options;
}
